import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleLocation } from "@common/ADAPT.Common.Model/organisation/role-location";
import { RoleType } from "@common/ADAPT.Common.Model/organisation/role-type";
import { AssignableRoleTypes, CoachRoleTypes, RoleTypeCode, RoleTypeCodeLabel } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { Node } from "devextreme/ui/tree_view";
import { DxTreeViewComponent } from "devextreme-angular";
import { EMPTY, filter, forkJoin, lastValueFrom, switchMap } from "rxjs";
import { catchError, finalize, startWith, tap } from "rxjs/operators";

interface IRoleTreeItem {
    id: string;
    text: string;
    expanded?: boolean;
    items?: IRoleTreeItem[];
    roleType?: RoleType;
    role?: Role;
}

@Component({
    selector: "adapt-team-roles-pin-existing-role-button",
    standalone: false,
    templateUrl: "./team-roles-pin-existing-role-button.component.html",
    styleUrl: "./team-roles-pin-existing-role-button.component.css",
})
export class TeamRolesPinExistingRoleButtonComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public team!: Team;
    @Output() public roleLocationsAdded = new EventEmitter<Role[]>();

    public treeViewDataSource: IRoleTreeItem[] = [];
    public selectedRoles: Role[] = [];

    public popupVisible = false;
    public saving = false;

    @ViewChild(DxTreeViewComponent) public treeViewComponent?: DxTreeViewComponent;

    public constructor(
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private directorySharedService: DirectorySharedService,
        private commonIntegratedArchitectureService: CommonIntegratedArchitectureFrameworkService,
        private rxjsBreezeService: RxjsBreezeService,
    ) {
        super();
    }

    public ngOnInit() {
        this.rxjsBreezeService.entityTypeChanged(Role).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.rxjsBreezeService.entityTypeChanged(RoleLocation).pipe(
            startWith(undefined),
            filter((roleLocation) => !roleLocation || roleLocation.teamId === this.team.teamId),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private async updateData() {
        const teamRoleLocations = await lastValueFrom(this.commonIntegratedArchitectureService.getRoleLocationsForTeam(this.team.teamId));
        const teamRoleIds = teamRoleLocations.map((rl) => rl.roleId);

        const roleTypeCodes = [...AssignableRoleTypes, ...CoachRoleTypes, RoleTypeCode.CulturalLeader];

        const roleTypes = (await lastValueFrom(this.commonIntegratedArchitectureService.getRoleTypesByCodes(roleTypeCodes)))
            .sort((rt1, rt2) => RoleTypeCodeLabel.ordinal(rt1.code) - RoleTypeCodeLabel.ordinal(rt2.code));

        const rolePredicate = new MethodologyPredicate<Role>("roleType.code", "in", roleTypeCodes)
            // exclude roles that are already pinned to the team
            .and(new MethodologyPredicate<Role>("roleId", "in", teamRoleIds).not());
        const roles = await this.directorySharedService.promiseToGetActiveRolesByPredicate(rolePredicate);

        this.treeViewDataSource = [];
        const roleTypesGroupedByLabel = ArrayUtilities.groupArrayBy(roleTypes, (roleType) => RoleTypeCodeLabel.label(roleType.code));
        for (const roleTypeGroup of roleTypesGroupedByLabel) {
            const allRoles = roles.filter((role) => roleTypeGroup.items.includes(role.roleType!))
                .sort(SortUtilities.getSortByFieldFunction<Role>("label"));

            if (allRoles.length === 0) {
                continue;
            }

            const editableRoleType = roleTypeGroup.items.find((rt) => AssignableRoleTypes.includes(rt.code));

            this.treeViewDataSource.push({
                id: roleTypeGroup.key,
                text: roleTypeGroup.key,
                expanded: true,
                roleType: editableRoleType,
                items: allRoles.map((role) => ({
                    id: `${roleTypeGroup.key}_${role.roleId}`,
                    text: role.label,
                    role,
                    roleType: role.roleType ?? undefined,
                })),
            });
        }
    }

    public onTreeViewSelectionChanged(selectedNodes: Node<IRoleTreeItem>[]) {
        this.selectedRoles = selectedNodes
            .map((node) => (node.itemData as IRoleTreeItem).role)
            .filter((role) => !!role);
    }

    @Autobind
    public onAddClicked() {
        if (this.selectedRoles.length === 0) {
            return EMPTY;
        }

        this.saving = true;
        return forkJoin(this.selectedRoles.map((role) => this.commonIntegratedArchitectureService.createRoleLocationForTeam(role, this.team))).pipe(
            switchMap((roleLocations) => this.commonDataService.saveEntities(roleLocations).pipe(
                catchError(() => this.commonDialogService.handleSaveFailure(roleLocations)),
            )),
            tap(() => {
                this.roleLocationsAdded.emit(this.selectedRoles);
                this.cancel();
            }),
            switchMap(() => this.updateData()),
            finalize(() => this.saving = false),
        );
    }

    public cancel() {
        this.popupVisible = false;
        this.selectedRoles = [];

        // clear selection when cancelling
        this.treeViewComponent?.instance?.unselectAll();
    }
}
